import React, { useEffect, useState } from 'react'

import { Modal } from '../../../../components/Modal/Modal'
import { Col, Container, Form, Modal as BootstrapModal, Row, Stack, Tab } from 'react-bootstrap'

import { CRMIntegrationSettings } from '../CRMIntegrationSettings'
import { Tabs } from '../../../../components/Tabs/Tabs'
import { Button } from '../../../../components/Button/Button'
import { merge } from '../../../Integration/utils'
import { removeEmptyItems } from '../../../../common/util'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { CheckpointRequest, useCreateCheckpointMutation, useGetCheckpointQuery, useUpdateCheckpointMutation } from '../../../../store/sync/sync'
import { useGetMeQuery, useUpdateUserIntegrationMutation } from '../../../../store/user/user'
import { IntegrationServiceId } from '../../../../common/integrations'
import { Controller, useForm } from 'react-hook-form'

import { ScopedBlock } from '../../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../../components/ScopedBlock/models'
import { FormFeedback } from '../../../../components/Form/FormFeedback'
import {
  dynamicsSettingsDefaultValues,
  DynamicsSettingsFormValues,
} from '../../../Integration/IntegrationManagement/IntegrationSpecificSettings/DynamicsBased/models'
import { useGetAccountQuery } from '../../../../store/accountUserSettings/accountUserSettings'
import { getAccountCRMIntegrationConfig } from '../../../../models/Account'

export type DynamicsSettingsModalProps = {
  show: boolean
  onHide: () => void
}

export const DynamicsSettingsModal: React.FC<DynamicsSettingsModalProps> = ({ show, onHide }) => {
  const [activeTabKey, setActiveTabKey] = useState<string>('dynamicsMappings')
  const changeTab = (key: string | null) => {
    if (key) {
      setActiveTabKey(key)
    }
  }

  const DynamicsSettingsModal = () => {
    return (
      <Tabs activeKey={activeTabKey} id='dynamics-settings' className='d-flex mb-3' onSelect={changeTab} mountOnEnter unmountOnExit>
        <Tab eventKey='dynamicsMappings' title='Mappings'>
          <CRMIntegrationSettings serviceId='dynamics' onSaved={onHide} onCancelled={onHide} />
        </Tab>
        <Tab eventKey='dynamicsSyncSettings' title='Contact Sync'>
          <DynamicsContactSyncSettings />
        </Tab>
      </Tabs>
    )
  }
  return (
    <Modal
      show={show}
      onHide={onHide}
      header={<BootstrapModal.Title>Edit Dynamics Integration Settings</BootstrapModal.Title>}
      body={<DynamicsSettingsModal />}
    />
  )
}

const DynamicsContactSyncSettings = () => {
  const accountId = useAppSelector(selectAccountId)
  const [autoSync, setAutoSync] = useState<boolean | null>(false)
  const { data: account, isFetching: isAccountFetching } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: 10 })
  const [isSyncDisabled, setIsSyncDisabled] = useState<boolean>(true)
  const checkpointRequest = {
    accountId,
    level: 'user',
    connector: 'dynamics',
    direction: 'inbound',
    object: 'contact_operation',
  } as CheckpointRequest
  const { data: checkpoint, isFetching: isGetCheckpointFetching } = useGetCheckpointQuery(checkpointRequest)
  const [isUpdating, setIsUpdating] = useState<boolean>(false)
  const [createCheckpoint, { isLoading: isCreateCheckpointLoading }] = useCreateCheckpointMutation()
  const { data: userData, isFetching: isGetMeQueryFetching } = useGetMeQuery({ accountId: accountId })
  const form = useForm<DynamicsSettingsFormValues>({ defaultValues: dynamicsSettingsDefaultValues })
  const [updateCheckpoint, { isLoading: isUpdateCheckpointLoading }] = useUpdateCheckpointMutation()
  const [updateUserIntegration, { isLoading: isUpdateUserIntegrationLoading }] = useUpdateUserIntegrationMutation()

  useEffect(() => {
    if (account && account.data) {
      const serviceIntegration = getAccountCRMIntegrationConfig(account.data, 'dynamics')
      setIsSyncDisabled(serviceIntegration?.isSyncDisabled ?? false)
    }
  }, [account])

  useEffect(() => {
    setAutoSync(checkpoint?.data?.active ?? null)
  }, [checkpoint])

  useEffect(() => {
    form.setValue('inbound.contact.autoSync', autoSync || false)
  }, [autoSync, form.setValue, form])

  if (isGetCheckpointFetching) {
    return (
      <>
        <div className='skeleton mb-2' style={{ width: '100%', height: '70px' }} />
        <div className='skeleton mb-3' style={{ width: '100%', height: '70px' }} />
      </>
    )
  }

  const onSubmit = async (data: DynamicsSettingsFormValues) => {
    try {
      setIsUpdating(true)
      if (autoSync == null) {
        if (data.inbound.contact.autoSync) {
          await createCheckpoint(checkpointRequest).unwrap()
        }
      } else {
        await updateCheckpoint({ ...checkpointRequest, active: data.inbound.contact.autoSync }).unwrap()
      }
      const integration = userData?.integrations?.find((i) => i.serviceId === IntegrationServiceId.DYNAMICS)
      if (integration) {
        let filters: { [key: string]: any } = {}
        if (data.inbound.contact.filters.active !== undefined && data.inbound.contact.filters.active !== null) {
          filters.active = data.inbound.contact.filters.active
        } else {
          filters.active = null
        }
        if (data.inbound.contact.filters.contactType) {
          filters.contactType = data.inbound.contact.filters.contactType
        } else {
          filters.contactType = null
        }
        if ((data.inbound.contact.filters.tags ?? []).length > 0) {
          filters.tags = data.inbound.contact.filters.tags
        } else {
          filters.tags = null
        }

        const serviceConfig = merge(integration.serviceConfig || {}, { inbound: { contact: { filters } } })

        await updateUserIntegration({
          accountId,
          integrationName: integration.serviceId,
          integrationId: integration.id,
          payload: { serviceConfig: removeEmptyItems(serviceConfig) },
        })
      }
    } catch (e) {
      console.error(e)
    } finally {
      setIsUpdating(false)
    }
  }

  const handleCancel = () => {
    form.reset()
  }

  return (
    <Container>
      <h6>Contacts</h6>
      <Row className='mb-1 mt-3'>
        <Col>
          <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
            <div>Synchronize your contacts automatically</div>
          </Stack>
        </Col>
        <Col md='auto'>
          <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
            <Controller
              name='inbound.contact.autoSync'
              control={form.control}
              render={({ field, fieldState }) => (
                <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
                  <Form.Switch checked={field.value === true} onChange={field.onChange} disabled={isSyncDisabled || isAccountFetching} />
                  {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                </ScopedBlock>
              )}
            />
          </Stack>
        </Col>
      </Row>

      <Row className='mt-3'>
        <Col>
          <div className='d-flex gap-3 justify-content-end mb-4'>
            <Button
              variant='secondary'
              onClick={handleCancel}
              disabled={isCreateCheckpointLoading || isUpdateCheckpointLoading || isUpdateUserIntegrationLoading}
            >
              Reset
            </Button>
            <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
              <Button
                variant='primary'
                onClick={form.handleSubmit(onSubmit)}
                loading={isCreateCheckpointLoading || isUpdateCheckpointLoading || isUpdateUserIntegrationLoading}
              >
                Save
              </Button>
            </ScopedBlock>
          </div>
        </Col>
      </Row>
    </Container>
  )
}
